'use client';

import {useState, type FormEvent} from 'react';

import type {FormValues, ShippingQuoteSubmitFormProps as Props} from './typings';

import SubmitButton from '@/atoms/Buttons/Submit/SubmitButton';
import ListDropdown from '@/molecules/Dropdowns/List/ListDropdown';
import Textfield from '@/molecules/Textfield/Textfield';
import configs from '@/utils/configs';
import AddressSummary from './AddressSummary';
import {StyledForm, StyledFormActions, StyledFormControl} from './styles';

const getInitialFormValues = (country?: string): FormValues => ({
  country: country || '',
  city: '',
});

const isFormValid = ({country, city}: FormValues) => country.trim() !== '' && city.trim() !== '';

/**
 * @description Form to submit shipping quote request.
 * - Select destination country and enter city.
 * @param {Props} props - component props.
 * @param {Function} props.onSubmit - Function to handle form submission.
 * @param {boolean} props.isDisabled - Flag to disable the form inputs and submission.
 * @param {string} props.initialCountry - (optional) Initial country code to pre-fill the country selection.
 * @param {Object} props.origin - The origin address object.
 * @returns {JSX.Element} The rendered shipping quote submission form.
 */
const ShippingQuoteSubmitForm = ({
  onSubmit,
  isDisabled,
  initialCountry,
  origin,
}: Props): JSX.Element => {
  const [formValues, setFormValues] = useState(() => getInitialFormValues(initialCountry));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isDisabled || !isFormValid(formValues)) return;

    onSubmit(formValues);
  };

  return (
    <StyledForm onSubmit={handleSubmit} aria-label="Shipping Quote Submission Form">
      <div>
        <AddressSummary location={origin} variant="origin" aria-label="Origin Address Summary" />
      </div>
      <StyledFormControl>
        <label>Select Destination Country</label>
        <ListDropdown
          options={configs.countries}
          callback={(value) => setFormValues({...formValues, country: value})}
          selected={formValues.country}
          title="Select Country"
          searchable
          wide
          type="button"
        />
      </StyledFormControl>
      <StyledFormControl>
        <Textfield
          id="city"
          label="Destination City"
          value={formValues.city}
          callback={(value) => {
            setFormValues({...formValues, city: value});
          }}
          disabled={isDisabled}
          required
          placeholder='e.g., "London"'
        />
      </StyledFormControl>
      <StyledFormActions>
        <SubmitButton
          title="Get Shipping Estimate"
          style="secondary"
          disabled={isDisabled || !isFormValid(formValues)}
        >
          Get Shipping Estimate
        </SubmitButton>
      </StyledFormActions>
    </StyledForm>
  );
};

export default ShippingQuoteSubmitForm;
